<template>
  <expandable-material-card
    :title="$t('components.plancreationwizard.general_settings')"
    :color="$vuetify.theme.themes.light.secondary"
    icon="settings"
  >
    <v-card-text>
      <v-form v-model="valid" v-if="portal !== null && portal !== undefined">
        <v-layout row wrap>
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-autocomplete
              v-model="portal.languages"
              :label="$t('components.portal.portal_settings.languages')"
              :items="languages"
              item-text="title"
              item-value="language"
              multiple
              class="required"
              :rules="[$rules.atLeastOne]"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <no-autocomplete-textfield
              :label="$t('headers.claim_submissions.plan_number')"
              :value="portal.planNumber"
              disabled
            ></no-autocomplete-textfield>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-checkbox
              :label="$t('views.plangeneralparameters.has_beneficiaries')"
              v-model="portal.hasBeneficiaries"
            ></v-checkbox>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-checkbox
              :label="$t('views.plangeneralparameters.must_add_beneficiaries')"
              v-model="portal.mustAddBeneficiaries"
            ></v-checkbox>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-checkbox
              :label="$t('views.plangeneralparameters.restrict_email_on_registration')"
              v-model="portal.restrictEmailOnRegistraton"
            ></v-checkbox>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="3"
            class="pt-0 pb-0"
            v-for="lang in languages"
            :key="`planAdministratorName${lang.language}`"
          >
            <no-autocomplete-textfield
              v-if="lang.language.toLowerCase() === 'en'"
              :label="
                $t(
                  `components.portal.plan_settings.plan_administrator_name_{0}`,
                  [lang.language.toUpperCase()]
                )
              "
              v-model="portal.planAdministratorName"
            ></no-autocomplete-textfield>
            <no-autocomplete-textfield
              v-else
              v-model="
                portal[
                  `planAdministratorName${$helpers.toPascalCase(lang.language)}`
                ]
              "
              :label="
                $t(
                  `components.portal.plan_settings.plan_administrator_name_{0}`,
                  [lang.language.toUpperCase()]
                )
              "
            ></no-autocomplete-textfield>
          </v-col>
          <v-col xs="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <no-autocomplete-textfield
              v-model="domain"
              :label="$t('components.portal.portal_content.domain')"
              :rules="[$rules.required]"
              class="required"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
            class="pt-0 pb-0"
            v-for="lang in languages"
            :key="`planAdministrator${lang.language}`"
          >
            <no-autocomplete-textfield
              v-if="lang.language.toLowerCase() === 'en'"
              :label="
                $t(`components.portal.plan_settings.plan_administrator_{0}`, [
                  lang.language.toUpperCase(),
                ])
              "
              v-model="portal.planAdministrator"
              :rules="
                $helpers.isRequired(lang.language, portal.languages)
                  ? [$rules.required]
                  : []
              "
              :class="
                $helpers.isRequired(lang.language, portal.languages)
                  ? 'required'
                  : ''
              "
              :clearable="!$helpers.isRequired(lang.language, portal.languages)"
            ></no-autocomplete-textfield>
            <no-autocomplete-textfield
              v-else
              v-model="
                portal[
                  `planAdministrator${$helpers.toPascalCase(lang.language)}`
                ]
              "
              :label="
                $t(`components.portal.plan_settings.plan_administrator_{0}`, [
                  lang.language.toUpperCase(),
                ])
              "
              :rules="
                $helpers.isRequired(lang.language, portal.languages)
                  ? [$rules.required]
                  : []
              "
              :class="
                $helpers.isRequired(lang.language, portal.languages)
                  ? 'required'
                  : ''
              "
              :clearable="!$helpers.isRequired(lang.language, portal.languages)"
            ></no-autocomplete-textfield>
          </v-col>
          <v-col cols="12" sm="6" md="4" class="pt-0 pb-0">
            <span>
              {{ $t("components.portal.plan_settings.plan_sponsor_address") }}
            </span>
            <canada-post-address
              :address="portal.planSponsorAddress"
              @update-address="updateAddress"
              @valid="validAddress = $event"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="3"
            class="pt-0 pb-0"
            v-for="lang in languages"
            :key="`planAdministratorWebsite${lang.language}`"
          >
            <no-autocomplete-textfield
              v-if="lang.language.toLowerCase() === 'en'"
              :label="
                $t(
                  `components.portal.plan_settings.plan_administrator_website_{0}`,
                  [lang.language.toUpperCase()]
                )
              "
              v-model="portal.planAdministratorWebsite"
              :rules="
                $helpers.isRequired(lang.language, portal.languages)
                  ? [$rules.required]
                  : []
              "
              :class="
                $helpers.isRequired(lang.language, portal.languages)
                  ? 'required'
                  : ''
              "
              :clearable="!$helpers.isRequired(lang.language, portal.languages)"
            ></no-autocomplete-textfield>
            <no-autocomplete-textfield
              v-else
              v-model="
                portal[
                  `planAdministratorWebsite${$helpers.toPascalCase(
                    lang.language
                  )}`
                ]
              "
              :label="
                $t(
                  `components.portal.plan_settings.plan_administrator_website_{0}`,
                  [lang.language.toUpperCase()]
                )
              "
              :rules="
                $helpers.isRequired(lang.language, portal.languages)
                  ? [$rules.required]
                  : []
              "
              :class="
                $helpers.isRequired(lang.language, portal.languages)
                  ? 'required'
                  : ''
              "
              :clearable="!$helpers.isRequired(lang.language, portal.languages)"
            ></no-autocomplete-textfield>
          </v-col>
          <v-col cols="12" sm="6" md="3" class="pt-0 pb-0">
            <no-autocomplete-textfield
              :label="$t(`components.portal.plan_settings.certificate_digits`)"
              v-model="portal.certificateDigits"
              clearable
              type="number"
              min="0"
            ></no-autocomplete-textfield>
          </v-col>
          <v-col cols="12" sm="6" md="3" class="pt-0 pb-0">
            <no-autocomplete-textfield
              :label="$t(`components.portal.plan_settings.allowed_months_of_claiming`)"
              v-model="portal.allowedMonthsOfClaiming"
              clearable
              type="number"
              min="0"
            ></no-autocomplete-textfield>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-checkbox
              v-model="portal.isDemoMockPlan"
              :label="$t('components.portal.plan_settings.demo_mock_plan')"
            ></v-checkbox>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
            lg="3"
            class="pt-0 pb-0"
            v-if="portal.isDemoMockPlan"
          >
            <v-checkbox
              v-model="portal.hasDemoHSA"
              :label="$t('components.portal.plan_settings.demo_hsa')"
            ></v-checkbox>
          </v-col>
          <!-- <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <no-autocomplete-textfield
              :label="$t('components.portal.plan_settings.plan_identity_id')"
              :value="portal.planIdentityId"
              disabled
            ></no-autocomplete-textfield>
          </v-col> -->
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-checkbox
              v-model="portal.isPensionsPortal"
              :label="$t('components.portal.plan_settings.pensions_portal')"
              disabled
            ></v-checkbox>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
            lg="3"
            class="pt-0 pb-0"
            v-if="portal.isPensionsPortal"
          >
            <v-checkbox
              v-model="portal.restrictRegisterEmails"
              :label="
                $t('components.portal.plan_settings.restrict_register_emails')
              "
            ></v-checkbox>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-checkbox
              v-model="portal.hasProviderSearch"
              :label="
                $t('components.portal.portal_settings.has_providers_search')
              "
            ></v-checkbox>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-checkbox
              v-model="portal.hasDrugSearch"
              :label="$t('components.portal.portal_settings.has_drugs_search')"
            ></v-checkbox>
          </v-col>
          <v-col
            xs="12"
            sm="6"
            md="4"
            lg="3"
            class="pt-0 pb-0"
            v-if="useLegacyClaims"
          >
            <v-checkbox
              readonly
              v-model="useLegacyClaims"
              :label="$t('components.portal.portal_content.use_legacy')"
            />
          </v-col>

          <!-- </v-layout>
        <v-layout row wrap> -->

          <!-- <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-checkbox
              v-model="portal.isLocalPlan"
              :label="$t('components.portal.portal_settings.local_plan')"
            ></v-checkbox>
          </v-col> -->
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-checkbox
              v-model="portal.enableClaims"
              :label="$t('components.portal.plan_settings.activate_claims')"
            ></v-checkbox>
          </v-col>
          <v-col cols="12" sm="6" class="pt-0 pb-0" v-if="!portal.enableClaims">
            <trans-textarea
              :value.sync="portal.enableClaimsMessage"
              :label="
                $t('components.portal.plan_settings.activate_claims_message')
              "
            ></trans-textarea>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-checkbox
              v-model="portal.memberHasSickPay"
              :label="$t('components.portal.plan_settings.member_has_sick_pay')"
            ></v-checkbox>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-checkbox
              v-model="portal.canEditDependants"
              :label="$t('components.portal.plan_settings.online_enrolment')"
            ></v-checkbox>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-checkbox
              v-model="portal.hasPreferredProviders"
              :label="
                $t('components.portal.plan_settings.has_preferred_providers')
              "
            ></v-checkbox>
          </v-col>

          <!-- </v-layout>
        <v-layout row wrap> -->
          <v-col
            cols="12"
            sm="6"
            md="3"
            class="pt-0 pb-0"
            v-for="lang in languages"
            :key="`logo${lang.language}`"
          >
            <image-picker
              v-if="lang.language.toLowerCase() === 'en'"
              :value.sync="portal.logo"
              :label="
                $t(`components.portal.portal_settings.logo_{0}`, [
                  lang.language.toUpperCase(),
                ])
              "
              :rules="
                $helpers.isRequired(lang.language, portal.languages)
                  ? [$rules.required]
                  : []
              "
              :class="
                $helpers.isRequired(lang.language, portal.languages)
                  ? 'required'
                  : ''
              "
              :clearable="!$helpers.isRequired(lang.language, portal.languages)"
            ></image-picker>
            <image-picker
              v-else
              :value.sync="
                portal[`logo${$helpers.toPascalCase(lang.language)}`]
              "
              :label="
                $t(`components.portal.portal_settings.logo_{0}`, [
                  lang.language.toUpperCase(),
                ])
              "
              :rules="
                $helpers.isRequired(lang.language, portal.languages)
                  ? [$rules.required]
                  : []
              "
              :class="
                $helpers.isRequired(lang.language, portal.languages)
                  ? 'required'
                  : ''
              "
              :clearable="!$helpers.isRequired(lang.language, portal.languages)"
            ></image-picker>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <image-picker
              :value.sync="portal.favicon"
              :label="$t(`components.portal.portal_settings.favicon`)"
              :rules="[$rules.required]"
              class="required"
            ></image-picker>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-checkbox
              :label="$t(`components.portal.portal_settings.can_optout`)"
              v-model="portal.canOptout"
            ></v-checkbox>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-text-field
              :label="$t(`components.portal.portal_settings.short_name_for_id`)"
              v-model="portal.shortNameForID"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3" class="pt-0 pb-0">
            <v-text-field
              :label="$t(`components.portal.portal_settings.allowed_email_domains`)"
              v-model="portal.allowedEmailDomains"
              :hint="$t(`components.portal.portal_settings.email_domains_indication`)"
            ></v-text-field>
          </v-col>
        </v-layout>
      </v-form>
      <v-card-actions>
        <v-spacer />
        <v-btn :disabled="allExpanded" text @click="all">
          {{ $t("globals.expand_all") }}
        </v-btn>
        <v-btn :disabled="allCollapsed" text @click="none">
          {{ $t("globals.collapse_all") }}
        </v-btn>
      </v-card-actions>
      <v-expansion-panels v-model="panel" style="margin-top: 10px" multiple>
        <plan-information-table
          :items="dataContainer.portal.planInformation"
          @update="$emit('update-plan-information', $event)"
          :selectedLanguages="dataContainer.portal.languages"
        />
      </v-expansion-panels>
    </v-card-text>
  </expandable-material-card>
</template>

<script>
import { mapGetters } from "vuex";
import NoAutocompleteTextfield from "@/components/NoAutocompleteTextfield";
import TransTextarea from "./TransTextarea.vue";
import ImagePicker from "./ImagePicker";
import ExpandableMaterialCard from "./ExpandableMaterialCard.vue";
import PlanInformationTable from "./PlanInformationTable.vue";
import ContractInformationTable from "./ContractInformationTable.vue";
import CanadaPostAddress from "./CanadaPostAddress.vue";
export default {
  components: {
    NoAutocompleteTextfield,
    TransTextarea,
    ImagePicker,
    ExpandableMaterialCard,
    ContractInformationTable,
    PlanInformationTable,
    CanadaPostAddress,
  },
  props: ["dataContainer", "payload"],
  data() {
    return {
      valid: true,
      portal: null,
      panel: [0, 1],
      items: 2,
      domain: null,
      useLegacyClaims: false,
      validAddress: true,
    };
  },
  computed: {
    ...mapGetters("common", ["language", "languages"]),
    ...mapGetters("plansMembers", ["options"]),
    ...mapGetters("plansMembers", ["planIdentity", "planOptions"]),
    allExpanded() {
      return (
        JSON.stringify(this.panel) ===
        JSON.stringify([...Array(this.items).keys()].map((k, i) => i))
      );
    },
    allCollapsed() {
      return JSON.stringify(this.panel) === JSON.stringify([]);
    },
  },
  watch: {
    dataContainer: {
      handler(v) {
        if (v !== null && v !== undefined) {
          // console.log("general data container change: ", JSON.stringify(v));
          this.portal = JSON.parse(JSON.stringify(v.portal));
        }
      },
      deep: false,
    },
    payload: {
      handler(v) {
        if (v !== null && v !== undefined) {
          this.domain = v.Domain;
          this.useLegacyClaims = v.UseLegacyClaims;
        }
      },
    },
    portal: {
      handler(v) {
        console.log("general update", JSON.stringify(v));
        this.$emit("update", v);
      },
      deep: true,
    },
    valid: {
      handler(v) {
        this.$emit("valid", v && this.validAddress);
      },
    },
    validAddress: {
      handler(v) {
        this.$emit("valid", v && this.valid);
      },
    },
    domain: {
      handler(v) {
        this.$emit("domain", v);
      },
    },
    useLegacyClaims: {
      handler(v) {
        this.$emit("legacy", v);
      },
    },
  },
  mounted() {
    // console.log("general mounted: ", JSON.stringify(this.dataContainer));
    if (this.dataContainer !== null && this.dataContainer !== undefined) {
      this.portal = JSON.parse(JSON.stringify(this.dataContainer.portal));
      if (this.payload !== null && this.payload !== undefined) {
        this.domain = this.payload.Domain;
        this.useLegacyClaims = this.payload.UseLegacyClaims;
      }
      this.all();
      this.$nextTick(() => {
        this.$emit("valid", this.valid && this.validAddress);
      });
    }
  },
  methods: {
    all() {
      this.panel = [...Array(this.items).keys()].map((k, i) => i);
    },
    none() {
      this.panel = [];
    },
    updateAddress(address) {
      console.log(address);
      this.portal.planSponsorAddress = address;
      this.$emit("update", this.portal);
      this.$emit("valid", this.valid && this.validAddress);
    },
  },
};
</script>
